import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { User, UserCreateRequest, UserUpdateRequest, UserRole } from '../types/api';
import { usersNew, apiServices } from '../services/api';
import { useCacheConfig } from '../contexts/cache-config-context';

/**
 * Elenco "semplice" utenti (id, fullName, email, colorTag) per Select/MultiSelect assegnatari
 * Kanban. `getCacheTime('users')` esiste già in DEFAULT_CACHE_TIMES (10 minuti).
 */
export const useSimpleUsers = () => {
  const { getCacheTime } = useCacheConfig();

  return useQuery({
    queryKey: ['users', 'simple'],
    queryFn: () => usersNew.getSimpleUsers(),
    staleTime: getCacheTime('users'),
  });
};

export const useAllUsers = () => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['users'],
    queryFn: () => usersNew.getAllUsers(),
    staleTime: getCacheTime('users'),
  });
};

export const useUser = (id: string) => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['users', id],
    queryFn: () => usersNew.getUserById(id),
    staleTime: getCacheTime('users'),
    enabled: !!id,
  });
};

export const useCurrentUser = () => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['users', 'current'],
    queryFn: () => apiServices.auth.getCurrentUser(), // Keep using legacy auth service
    staleTime: getCacheTime('users'),
  });
};

export const useUsersByRole = (role: UserRole) => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['users', 'role', role],
    queryFn: () => usersNew.getUsersByRole(role),
    staleTime: getCacheTime('users'),
  });
};

export const useSearchUsers = (searchTerm: string) => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['users', 'search', searchTerm],
    queryFn: () => usersNew.searchUsers({ searchTerm }),
    staleTime: getCacheTime('users'),
    enabled: !!searchTerm && searchTerm.length >= 2,
  });
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (newUser: UserCreateRequest) => usersNew.createUser(newUser),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, user }: { id: string; user: UserUpdateRequest }) =>
      usersNew.updateUser(id, user),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users', variables.id] });
    },
  });
};

export const useUpdateUserProfile = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, user }: { id: string; user: Partial<User> }) => 
      usersNew.updateUser(id, user as UserUpdateRequest),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users', variables.id] });
    },
  });
};

export const useChangePassword = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ oldPassword, newPassword }: { oldPassword: string; newPassword: string }) =>
      apiServices.auth.changePassword(oldPassword, newPassword), // Keep using legacy auth service
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', 'current'] });
    },
  });
};

export const useToggleUserStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      active ? usersNew.activateUser(id) : usersNew.deactivateUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useAssignRole = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: UserRole }) =>
      usersNew.assignRole(userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => usersNew.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useRestoreUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => 
      usersNew.activateUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

// Hook aggiuntivi per le funzionalità specifiche del UserService
export const useUserStats = (id: string) => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['users', id, 'stats'],
    queryFn: () => usersNew.getUserStatistics(id),
    staleTime: getCacheTime('users'),
    enabled: !!id,
  });
};

// Rimuoviamo useResetUserPassword poiché non esiste nel nuovo servizio
// export const useResetUserPassword = () => { ... }

// Rimuoviamo useUploadUserAvatar poiché non esiste nel nuovo servizio  
// export const useUploadUserAvatar = () => { ... }

// Rimuoviamo useUserActivities poiché non esiste nel nuovo servizio
// export const useUserActivities = (id: string) => { ... }
